"use client";
import PrimaryBtn from "@/components/PrimaryBtn";
import { Icons } from "@/app/assets/shared";
import { data, AwardItem } from "../../app/main-header/data";
import QuoteButton from "@/components/QuoteButton";
import AwardCard from "./AwardCard";
import { useEffect, useRef, useState } from "react";

interface HeadingSlide {
  heading: string;
  desc: string;
  href: string;
}

interface HeadBannerProps {
  buttonTitle: string;
  headings?: HeadingSlide[];
  /**
   * Legacy props from before the multi-slide rotation existed. Still
   * accepted so existing call sites (like the CMS-driven bannerSection data)
   * keep compiling, but they are no longer used to build slides — pass
   * `headings` instead to override the default rotation.
   */
  heading?: string;
  desc?: string;
}

const SLIDE_DURATION = 5000; // ms per heading

/**
 * Each slide carries its own heading, description, and CTA link — swapping
 * the active slide updates all three together (heading/words, paragraph,
 * and where the "buttonTitle" button points to).
 */
const DEFAULT_SLIDES: HeadingSlide[] = [
  {
    heading: "Engineering AI-First Solutions\nwith Human-Led Strategy",
    desc: "We engineer AI-native digital solutions to help businesses enhance decision-making, automate complex workflows, and improve workflow efficiency. By integrating AI capabilities within business processes and digital products, we support organizations to adapt to evolving industry demands, build a strong-foundation for long-term growth, and overcome technical challenges.",
    href: "/ai-development-company",
  },
  {
    heading: "Transforming AI\nMVPs for Production",
    desc: "We help businesses transform AI-built MVPs into secure and scalable production-ready digital solutions. From performance optimization to architecture strengthening and deployment readiness, our experts ensure your AI products are prepared to support long-term business objectives, meet evolving market demands, and handle real-world user expectations.",
    href: "/ai-mvp-production-ready",
  },
  {
    heading: "Drive Innovation with\nMobile Applications",
    desc: "We design and develop high-performance mobile applications that support businesses to connect with their customers beyond their geographical limitations. From initial planning to UI/UX design, development, and ongoing support, our experts deliver engaging mobile experiences to streamline everyday operations and help businesses unlock new opportunities.",
    href: "/mobile-application-development",
  },
  {
    heading: "Building Experiences\nThat Drive Growth",
    desc: "Our experts engineer custom software as well as web solutions to modernize legacy systems, streamline workflows, and strengthen business operations. By aligning technology with custom business requirements, we help startups as well as large-scale enterprises adapt to changing market needs, improve efficiency, and prepare for future demands.",
    href: "/web-development",
  },
];

export default function index({
  headings,
  buttonTitle,
  // Accepted for backward compatibility with existing call sites (e.g. CMS
  // bannerSection data); intentionally unused now that slides are static.
  heading: _legacyHeading,
  desc: _legacyDesc,
}: HeadBannerProps) {
  // Prefer an explicit `headings` array; otherwise always use the default
  // 4-slide rotation. A legacy `heading`/`desc` prop pair (from before this
  // rotation existed) no longer collapses this into a single static slide —
  // pass `headings` explicitly if you want to override the defaults.
  const slides: HeadingSlide[] =
    headings && headings.length > 0 ? headings : DEFAULT_SLIDES;

  const [activeIndex, setActiveIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (slides.length <= 1) return;

    const interval = setInterval(() => {
      setIsFading(true);
      timeoutRef.current = setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % slides.length);
        setIsFading(false);
      }, 300); // fade-out duration before swapping text
    }, SLIDE_DURATION);

    return () => {
      clearInterval(interval);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [slides.length]);

  const activeSlide = slides[activeIndex];
  const HeadingTag = activeIndex === 0 ? "h1" : "span";
  const [primaryHeading, ...secondaryHeadingParts] =
    activeSlide.heading.split("\n");
  const secondaryHeading = secondaryHeadingParts.join(" ");

  const headingClass = `self-stretch justify-start text-white text-[clamp(32px,4.6875vw,90px)] font-normal clash-font capitalize leading-[100%] transition-opacity duration-300 ease-out ${
    isFading ? "opacity-0" : "opacity-100"
  }`;

  const paragraphClasses = `self-stretch justify-start text-white text-[clamp(32px,4.6875vw,90px)] font-normal clash-font capitalize leading-[110%] transition-opacity duration-300 ease-out ${
    isFading ? "opacity-0" : "opacity-100"
  }`;
  const descClass = `justify-start text-white/50 text-[clamp(16px,1.3542vw,26px)] max-w-[60ch] font-normal leading-[150%] tracking-tight py-4 transition-opacity duration-300 ease-out ${
    isFading ? "opacity-0" : "opacity-100"
  }`;

  return (
    <>
      <div className="flex flex-col gap-2 mt-auto text-center lg:text-left items-center lg:items-start">
        <HeadingTag className="block w-full text-center lg:text-left mt-auto m-0 p-0">
          {/* Mobile: below 640px */}
          <span className={`${headingClass} sm:hidden`}>
            {activeSlide.heading.replace(/\s*\n\s*/g, " ")}
          </span>

          {/* Tablet and desktop: 640px and above */}
          <span className="hidden sm:flex sm:flex-col sm:gap-2 lg:gap-2">
            <span className={headingClass}>{primaryHeading}</span>

            <span className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1 lg:flex-nowrap lg:justify-start lg:gap-x-6 lg:gap-y-0">
              <span className={paragraphClasses}>{secondaryHeading}</span>
            </span>
          </span>
        </HeadingTag>

        <p className={descClass}>{activeSlide.desc}</p>

        {/* New Request a Quote Button - Hidden above 1720px */}
        <div className="flex flex-col md:flex-row gap-1 md:gap-3">
          <PrimaryBtn
            text={buttonTitle}
            iconSrc={Icons?.chevronBlue}
            href={activeSlide.href}
          />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row justify-between items-center mt-auto pt-4 pb-10">
        <div className="awards-content flex flex-col w-full lg:w-auto items-center text-center lg:text-left lg:items-start">
          <p className="justify-start text-white text-[length:var(--banner-para)] max-w-[60ch] font-semibold  leading-[150%] tracking-tight">
            15+ Industry Prestigious Awards
          </p>
          <p className="opacity-80 justify-start text-white text-[length:var(--primarybtn)] font-normal  leading-[120%] tracking-tight">
            for excellence and innovation
          </p>
        </div>

        <div className="awards grid grid-cols-2 gap-y-2 gap-x-4 md:flex md:flex-wrap md:justify-center md:gap-8 lg:gap-4 pt-4 lg:pt-0 mx-auto lg:mr-0 max-w-full">
          {data?.map((item: AwardItem, index: number) => (
            <AwardCard key={index} award={item} />
          ))}
        </div>
      </div>

      {/* Heading rotation indicator: equal-width slides spanning full viewport width */}
      {slides.length > 1 && (
        <div className="relative left-1/2 -translate-x-1/2 hidden lg:flex w-screen gap-1">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Show heading ${index + 1}`}
              onClick={() => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
                setIsFading(false);
                setActiveIndex(index);
              }}
              className="relative h-[3px] flex-1 bg-white/20 overflow-hidden"
            >
              <span
                className="absolute left-0 top-0 h-full bg-white"
                style={{
                  width:
                    index < activeIndex
                      ? "100%"
                      : index > activeIndex
                        ? "0%"
                        : undefined,
                  animation:
                    index === activeIndex
                      ? `headSlideFill ${SLIDE_DURATION}ms linear forwards`
                      : undefined,
                }}
              />
            </button>
          ))}
        </div>
      )}

      <style jsx>{`
        @keyframes headSlideFill {
          from {
            width: 0%;
          }
          to {
            width: 100%;
          }
        }
      `}</style>
    </>
  );
}
